import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { vpAxios } from "./api";
import useVaipedeManifest from "./useVaipedeManifest";
import usePageMeta from "../../hooks/usePageMeta";
import {
  Loader2, ArrowRight, Check, Store, Smartphone, ClipboardList,
  UtensilsCrossed, DollarSign, Users, Lock, X, MessageCircle,
  ChevronLeft, ChevronRight,
} from "lucide-react";

const SHOWCASE = [
  { src: "/vaipede-showcase/1-sidebar.jpg", label: "10 módulos em um só app",          sub: "Mesas · PDV · Cozinha · Delivery · Caixa · Relatórios" },
  { src: "/vaipede-showcase/2-produtos.jpg", label: "Cardápio digital ilimitado",       sub: "Categorias · Preços · Fotos · Status ao vivo" },
  { src: "/vaipede-showcase/3-cardapio.jpg", label: "Cardápio digital do cliente",     sub: "Promoções em destaque · Busca · Categorias" },
  { src: "/vaipede-showcase/4-carrinho.jpg", label: "Adicionar ao pedido em 1 toque",  sub: "Total automático · Notificações instantâneas" },
  { src: "/vaipede-showcase/5-checkout.jpg", label: "Cadastro em 10 segundos",         sub: "Nome · WhatsApp · Localização GPS · Pronto!" },
];

/** Mockup de celular exibindo screenshots reais do VAI PEDE em carrossel. */
function ShowcaseCarousel() {
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  const total = SHOWCASE.length;

  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => setI((v) => (v + 1) % total), 3600);
    return () => clearInterval(t);
  }, [paused, total]);

  const go = (d) => setI((v) => (v + d + total) % total);
  const item = SHOWCASE[i];

  return (
    <div
      className="relative select-none"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      data-testid="vaipede-showcase-carousel"
    >
      {/* Blur decorativo atrás */}
      <div className="absolute -inset-6 bg-gradient-to-br from-red-100 via-orange-100 to-amber-50 rounded-[2.5rem] blur-2xl opacity-70 pointer-events-none"></div>

      {/* Frame do celular */}
      <div className="relative mx-auto max-w-[290px]">
        <div className="rounded-[2.5rem] bg-neutral-900 p-2.5 shadow-2xl shadow-red-900/20 border-[5px] border-neutral-800">
          {/* notch */}
          <div className="relative mx-auto w-24 h-5 bg-neutral-900 rounded-b-2xl z-10 -mt-0.5 mb-0.5"></div>
          <div className="relative overflow-hidden rounded-[2rem] bg-white aspect-[9/19]">
            {SHOWCASE.map((s, idx) => (
              <img
                key={s.src}
                src={s.src}
                alt={s.label}
                loading="lazy"
                className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${idx === i ? "opacity-100" : "opacity-0"}`}
                data-testid={`vaipede-showcase-slide-${idx}`}
              />
            ))}
            {/* Dots inside phone */}
            <div className="absolute bottom-3 left-0 right-0 flex items-center justify-center gap-1.5 z-10">
              {SHOWCASE.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setI(idx)}
                  aria-label={`Slide ${idx + 1}`}
                  className={`h-1.5 rounded-full transition-all ${idx === i ? "w-6 bg-[#EA1D2C]" : "w-1.5 bg-white/80 backdrop-blur"}`}
                  data-testid={`vaipede-showcase-dot-${idx}`}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Arrows externas */}
        <button onClick={() => go(-1)} aria-label="Anterior" className="absolute left-[-10px] top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white text-neutral-800 shadow-lg grid place-items-center hover:bg-neutral-50 hover:scale-105 transition" data-testid="vaipede-showcase-prev">
          <ChevronLeft className="w-5 h-5"/>
        </button>
        <button onClick={() => go(+1)} aria-label="Próximo" className="absolute right-[-10px] top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white text-neutral-800 shadow-lg grid place-items-center hover:bg-neutral-50 hover:scale-105 transition" data-testid="vaipede-showcase-next">
          <ChevronRight className="w-5 h-5"/>
        </button>
      </div>

      {/* Caption abaixo */}
      <div className="text-center mt-6 min-h-[64px]" data-testid="vaipede-showcase-caption">
        <div className="font-black text-lg">{item.label}</div>
        <div className="text-xs text-neutral-500 mt-1">{item.sub}</div>
      </div>
    </div>
  );
}

export default function VaiPedeLoginPage() {
  useVaipedeManifest();
  usePageMeta({
    title: "VAI PEDE — Cardápio digital, PDV e delivery para restaurantes · 30 dias grátis",
    description: "Sistema completo para lanchonetes e restaurantes: cardápio digital, PDV, cozinha, delivery, caixa e muito mais. Crie em 30 segundos. Grátis por 30 dias, sem cartão.",
    url: typeof window !== "undefined" ? window.location.origin + "/vaipede" : undefined,
  });
  const navigate = useNavigate();
  const [planos, setPlanos] = useState([]);
  const [suporteWhats, setSuporteWhats] = useState("");
  const [trialDias, setTrialDias] = useState(30);
  const [trialLimiteMes, setTrialLimiteMes] = useState(20);
  const [showAuth, setShowAuth] = useState(false);
  const [mode, setMode] = useState("login"); // login | register
  const [planoSelecionado, setPlanoSelecionado] = useState("start");
  const [form, setForm] = useState({ nome: "", email: "", senha: "", loja_nome: "", whatsapp: "" });
  const [loading, setLoading] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  useEffect(() => {
    vpAxios.get("/vaipede/planos-publico")
      .then(r => {
        setPlanos(r.data.planos || []);
        setSuporteWhats(r.data.suporte_whatsapp || "");
        if (r.data.trial_dias) setTrialDias(r.data.trial_dias);
        if (r.data.trial_expirado_limite_mes) setTrialLimiteMes(r.data.trial_expirado_limite_mes);
      })
      .catch(() => {});
  }, []);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const url = mode === "login" ? "/vaipede/auth/login" : "/vaipede/auth/register";
      const payload = mode === "login"
        ? { email: form.email, senha: form.senha }
        : { nome: form.nome, email: form.email, senha: form.senha, loja_nome: form.loja_nome, whatsapp: form.whatsapp };
      const { data } = await vpAxios.post(url, payload);
      localStorage.setItem("vaipede_token", data.token);
      localStorage.setItem("vaipede_user", JSON.stringify(data.user));
      if (mode === "register") {
        toast.success(`Conta criada! Você tem ${trialDias} dias grátis.`);
        navigate("/vaipede/boas-vindas");
      } else {
        toast.success("Bem-vindo!");
        navigate("/vaipede/dashboard");
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || "Falha na operação.";
      toast.error(typeof msg === "string" ? msg : "Erro");
    } finally { setLoading(false); }
  };

  const abrirCadastro = () => {
    setMode("register");
    setShowAuth(true);
  };

  const assinarPlano = async (planoId) => {
    const plano = planos.find(p => p.id === planoId);
    if (!plano) return;
    if (!plano.disponivel) {
      toast.info(`Plano ${plano.nome} chegando em breve!`);
      return;
    }
    setPlanoSelecionado(planoId);
    if (!form.nome || !form.email || !form.senha) {
      setMode("register");
      setShowAuth(true);
      return;
    }
    setCheckoutLoading(true);
    try {
      let token = null;
      try {
        const { data } = await vpAxios.post("/vaipede/auth/register", {
          nome: form.nome, email: form.email, senha: form.senha,
          loja_nome: form.loja_nome || form.nome, whatsapp: form.whatsapp,
        });
        token = data.token;
        localStorage.setItem("vaipede_token", token);
        localStorage.setItem("vaipede_user", JSON.stringify(data.user));
      } catch (regErr) {
        if (regErr?.response?.status === 409) {
          const { data } = await vpAxios.post("/vaipede/auth/login", { email: form.email, senha: form.senha });
          token = data.token;
          localStorage.setItem("vaipede_token", token);
          localStorage.setItem("vaipede_user", JSON.stringify(data.user));
        } else { throw regErr; }
      }
      const { data: chk } = await vpAxios.post("/vaipede/checkout", { plano: planoId });
      if (chk.status === "checkout_criado" && chk.init_point) {
        window.location.href = chk.init_point;
        return;
      }
      if (chk.status === "em_breve" || chk.status === "erro_mp") {
        toast.error(chk.message || "Checkout indisponível.");
        if (chk.whatsapp) window.open(chk.whatsapp, "_blank");
        navigate("/vaipede/dashboard");
        return;
      }
      navigate("/vaipede/dashboard");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Falha ao criar checkout";
      toast.error(typeof msg === "string" ? msg : "Erro");
    } finally { setCheckoutLoading(false); }
  };

  const features = [
    { icon: Store, title: "Mesas & Comandas" },
    { icon: Smartphone, title: "Cardápio Digital" },
    { icon: ClipboardList, title: "PDV Rápido" },
    { icon: UtensilsCrossed, title: "KDS da Cozinha" },
    { icon: DollarSign, title: "Caixa Automático" },
    { icon: Users, title: "Base de Clientes" },
  ];

  const suporteLink = suporteWhats
    ? `https://wa.me/${suporteWhats.replace(/\D/g, "")}?text=${encodeURIComponent("Olá! Quero conhecer o VAI PEDE.")}`
    : null;

  return (
    <div className="min-h-screen bg-white text-neutral-900" data-testid="vaipede-login">
      {/* Nav */}
      <nav className="border-b border-neutral-200 bg-white sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl grid place-items-center bg-[#EA1D2C] text-white font-black">V</div>
            <span className="font-black text-xl tracking-tight">VAI <span className="text-[#EA1D2C]">PEDE</span></span>
          </div>
          <div className="flex items-center gap-2">
            {suporteLink && (
              <a href={suporteLink} target="_blank" rel="noreferrer" className="hidden sm:inline-flex items-center gap-1.5 px-3 py-2 text-sm text-emerald-700 font-bold hover:bg-emerald-50 rounded-full transition" data-testid="vaipede-nav-suporte">
                <MessageCircle className="w-4 h-4"/> Suporte
              </a>
            )}
            <button onClick={() => { setMode("login"); setShowAuth(true); }} className="px-5 py-2 rounded-full bg-neutral-900 text-white font-bold hover:bg-black transition text-sm" data-testid="vaipede-nav-login">
              <Lock className="w-4 h-4 inline-block mr-1"/> Entrar
            </button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-4 pt-14 pb-16 grid md:grid-cols-2 gap-10 items-center">
        <div>
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-black mb-5 border border-emerald-200">
            🎁 {trialDias} dias grátis · sem cartão de crédito
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] tracking-tight">
            Comece em<br/>
            <span className="text-[#EA1D2C]">30 segundos.</span><br/>
            Grátis por {trialDias} dias.
          </h1>
          <p className="text-neutral-600 mt-6 text-lg max-w-xl">
            Crie sua conta agora e teste <b>todos os recursos</b> do VAI PEDE sem pagar nada: cardápio digital, PDV, cozinha, mesas, delivery, caixa e muito mais. Sem cartão. Sem compromisso.
          </p>
          <div className="flex flex-wrap gap-3 mt-8">
            <button onClick={abrirCadastro} className="px-7 py-4 rounded-full bg-[#EA1D2C] text-white font-black text-base hover:bg-[#c8121f] flex items-center gap-2 shadow-xl shadow-red-500/30 transition-all hover:scale-105" data-testid="vaipede-cta-comecar-gratis">
              Começar grátis <ArrowRight className="w-5 h-5"/>
            </button>
            <button onClick={() => document.getElementById("planos")?.scrollIntoView({behavior:"smooth"})} className="px-6 py-4 rounded-full bg-neutral-100 text-neutral-900 font-bold hover:bg-neutral-200 transition" data-testid="vaipede-cta-ver-planos">
              Ver planos
            </button>
            <button onClick={() => { setMode("login"); setShowAuth(true); }} className="px-6 py-4 rounded-full text-neutral-700 font-bold hover:bg-neutral-100 transition" data-testid="vaipede-cta-entrar">
              Entrar
            </button>
          </div>
          <div className="mt-6 flex flex-col gap-1 text-sm text-neutral-500">
            <div className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-600"/> Teste completo por {trialDias} dias · sem cartão</div>
            <div className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-600"/> Após o trial, use grátis até {trialLimiteMes} pedidos/mês</div>
            <div className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-600"/> Cancele a qualquer momento · Suporte no WhatsApp</div>
          </div>
        </div>

        <div className="relative">
          <ShowcaseCarousel />
        </div>
      </section>

      {/* Features */}
      <section className="bg-neutral-50 border-y border-neutral-200 py-14">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-2xl sm:text-3xl font-black text-center">Tudo que sua lanchonete precisa</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-8">
            {features.map(f => (
              <div key={f.title} className="bg-white rounded-2xl border border-neutral-200 p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-red-50 grid place-items-center text-[#EA1D2C] shrink-0">
                  <f.icon className="w-5 h-5"/>
                </div>
                <span className="font-bold text-sm">{f.title}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Planos */}
      <section id="planos" className="py-16">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-3xl sm:text-4xl font-black text-center">Escolha o plano ideal</h2>
          <p className="text-center text-neutral-600 mt-2 max-w-2xl mx-auto">Comece pelo Start e cresça com o VAI PEDE. Sem multa, sem fidelidade.</p>

          <div className="grid md:grid-cols-3 gap-4 mt-10">
            {planos.map(p => {
              const destaque = !!p.destaque;
              return (
                <div key={p.id} className={`relative rounded-3xl p-6 border-2 flex flex-col ${destaque ? "bg-neutral-900 text-white border-[#EA1D2C]" : "bg-white border-neutral-200"} ${!p.disponivel ? "opacity-90" : ""}`} data-testid={`vaipede-plano-${p.id}`}>
                  {destaque && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#EA1D2C] text-white text-xs font-black px-3 py-1 rounded-full">
                      MAIS POPULAR
                    </div>
                  )}
                  {p.em_breve && (
                    <div className="absolute -top-3 right-4 bg-amber-500 text-white text-xs font-black px-3 py-1 rounded-full">
                      EM BREVE
                    </div>
                  )}
                  {p.disponivel && !p.em_breve && (
                    <div className="absolute top-3 right-3 bg-emerald-500 text-white text-[10px] font-black px-2.5 py-1 rounded-full shadow-md flex items-center gap-1" data-testid={`vaipede-plano-trial-badge-${p.id}`}>
                      🎁 {trialDias} DIAS GRÁTIS
                    </div>
                  )}
                  <div className={destaque ? "text-white" : ""}>
                    <h3 className="text-2xl font-black">{p.nome}</h3>
                    <p className={`text-xs mt-1 italic ${destaque ? "text-neutral-300" : "text-neutral-500"}`}>"{p.foco}"</p>
                    <div className="flex items-baseline gap-1 mt-4">
                      <span className="text-4xl font-black">R$ {p.preco.toFixed(0)}</span>
                      <span className={destaque ? "text-neutral-400" : "text-neutral-500"}>/mês</span>
                    </div>
                    <p className={`text-[11px] mt-1 font-bold ${destaque ? "text-emerald-400" : "text-emerald-600"}`}>
                      Grátis por {trialDias} dias — sem cartão
                    </p>
                    <p className={`text-sm font-bold mt-2 ${destaque ? "text-[#EA1D2C]" : "text-[#EA1D2C]"}`}>{p.teto}</p>
                  </div>

                  <div className="mt-5 space-y-2 flex-1">
                    {p.recursos.map((r, i) => (
                      <div key={i} className="flex items-start gap-2 text-sm">
                        <Check className={`w-4 h-4 shrink-0 mt-0.5 ${destaque ? "text-emerald-400" : "text-emerald-600"}`}/>
                        <span className={destaque ? "text-neutral-200" : "text-neutral-700"}>{r}</span>
                      </div>
                    ))}
                  </div>

                  <button
                    onClick={() => assinarPlano(p.id)}
                    disabled={!p.disponivel || checkoutLoading}
                    className={`mt-6 w-full py-3 rounded-2xl font-black text-base transition flex items-center justify-center gap-2 ${
                      !p.disponivel
                        ? "bg-neutral-200 text-neutral-500 cursor-not-allowed"
                        : destaque
                          ? "bg-[#EA1D2C] hover:bg-[#c8121f] text-white"
                          : "bg-neutral-900 hover:bg-black text-white"
                    }`}
                    data-testid={`vaipede-btn-assinar-${p.id}`}
                  >
                    {checkoutLoading && planoSelecionado === p.id && <Loader2 className="w-4 h-4 animate-spin"/>}
                    {!p.disponivel ? "Em breve" : `Testar ${p.nome} grátis`}
                  </button>
                </div>
              );
            })}
          </div>

          <p className="text-center text-xs text-neutral-500 mt-8">
            🎁 {trialDias} dias grátis · 💳 Pagamento seguro via Mercado Pago · 🔒 Sem taxa de adesão · 🔄 Cancele quando quiser
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-neutral-200 py-8 text-center text-sm text-neutral-500">
        <div className="max-w-6xl mx-auto px-4 flex flex-col sm:flex-row items-center justify-between gap-3">
          <span>© {new Date().getFullYear()} VAI PEDE · Um produto Compromisso Digital</span>
          {suporteLink && (
            <a href={suporteLink} target="_blank" rel="noreferrer" className="text-emerald-700 font-bold flex items-center gap-1 hover:underline">
              <MessageCircle className="w-4 h-4"/> Falar com suporte
            </a>
          )}
        </div>
      </footer>

      {/* Modal de Auth */}
      {showAuth && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setShowAuth(false)} data-testid="vaipede-auth-modal">
          <div onClick={e => e.stopPropagation()} className="bg-white w-full max-w-md rounded-3xl shadow-2xl max-h-[94vh] overflow-y-auto">
            <div className="p-6 border-b border-neutral-100 flex items-center justify-between">
              <div>
                <h2 className="font-black text-2xl">{mode === "login" ? "Entrar" : "Criar conta grátis"}</h2>
                <p className="text-xs text-neutral-500 mt-0.5">
                  {mode === "login"
                    ? "Acesse sua loja"
                    : <span className="text-emerald-700 font-bold">✨ {trialDias} dias grátis · sem cartão</span>}
                </p>
              </div>
              <button onClick={() => setShowAuth(false)} className="p-2 hover:bg-neutral-100 rounded-full" data-testid="vaipede-auth-close"><X className="w-5 h-5"/></button>
            </div>
            <div className="p-6">
              <div className="flex mb-5 bg-neutral-100 rounded-full p-1">
                <button onClick={() => setMode("login")} className={`flex-1 py-2 rounded-full text-sm font-bold transition ${mode==="login" ? "bg-white shadow-sm" : "text-neutral-500"}`} data-testid="vaipede-tab-login">
                  Entrar
                </button>
                <button onClick={() => setMode("register")} className={`flex-1 py-2 rounded-full text-sm font-bold transition ${mode==="register" ? "bg-white shadow-sm" : "text-neutral-500"}`} data-testid="vaipede-tab-register">
                  Criar conta
                </button>
              </div>

              <form onSubmit={submit} className="space-y-3">
                {mode === "register" && (
                  <>
                    <input required value={form.nome} onChange={upd("nome")} placeholder="Seu nome completo *" className="w-full px-4 py-3 rounded-xl border border-neutral-200 focus:border-[#EA1D2C] outline-none" data-testid="vaipede-input-nome"/>
                    <input required value={form.loja_nome} onChange={upd("loja_nome")} placeholder="Nome da loja *" className="w-full px-4 py-3 rounded-xl border border-neutral-200 focus:border-[#EA1D2C] outline-none" data-testid="vaipede-input-loja"/>
                    <input required type="tel" inputMode="numeric" value={form.whatsapp} onChange={upd("whatsapp")} placeholder="WhatsApp da loja (com DDD) *" className="w-full px-4 py-3 rounded-xl border border-neutral-200 focus:border-[#EA1D2C] outline-none" data-testid="vaipede-input-whatsapp"/>
                  </>
                )}
                <input required type="email" value={form.email} onChange={upd("email")} placeholder={mode === "register" ? "Email *" : "Email"} className="w-full px-4 py-3 rounded-xl border border-neutral-200 focus:border-[#EA1D2C] outline-none" data-testid="vaipede-input-email"/>
                <input required type="password" value={form.senha} onChange={upd("senha")} placeholder={mode === "register" ? "Crie uma senha *" : "Senha"} className="w-full px-4 py-3 rounded-xl border border-neutral-200 focus:border-[#EA1D2C] outline-none" data-testid="vaipede-input-senha"/>

                <button type="submit" disabled={loading} className={`w-full py-4 rounded-xl font-black text-base disabled:opacity-60 flex items-center justify-center gap-2 transition ${mode === "register" ? "bg-[#EA1D2C] text-white hover:bg-[#c8121f] shadow-lg shadow-red-500/20" : "bg-neutral-900 text-white hover:bg-black"}`} data-testid="vaipede-btn-submit">
                  {loading && <Loader2 className="w-4 h-4 animate-spin"/>}
                  {mode === "login" ? "Entrar" : `Começar grátis por ${trialDias} dias`}
                </button>
              </form>

              {mode === "register" && (
                <div className="mt-4 space-y-1.5 text-xs text-neutral-500">
                  <div className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-emerald-600"/> Sem cartão · sem taxa de adesão</div>
                  <div className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-emerald-600"/> Acesso completo durante o trial</div>
                  <div className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-emerald-600"/> Depois: use grátis até {trialLimiteMes} pedidos/mês</div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
